import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Sparkles } from 'lucide-react';
import { Card } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { getReleaseNotes, markReleaseNoteAsRead, ReleaseNote } from '../../api/release-notes';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
}

export function WhatsNewSection() {
  const [releases, setReleases] = useState<ReleaseNote[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const fetchReleases = async () => {
      try {
        const data = await getReleaseNotes(3);
        setReleases(data);
        
        // Auto-expand on first visit if unread exists
        const hasUnread = data.some(r => !r.isRead);
        if (hasUnread && !hasAnimated) {
          setIsExpanded(true);
          setHasAnimated(true);
        }
      } catch (error) {
        console.error('Failed to fetch release notes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReleases();
  }, [hasAnimated]);

  const handleToggle = async () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);

    // Mark all as read when expanding
    if (newExpandedState && releases.some(r => !r.isRead)) {
      try {
        // Mark each unread release as read
        await Promise.all(
          releases
            .filter(r => !r.isRead)
            .map(r => markReleaseNoteAsRead(r.id))
        );
        
        // Update local state
        setReleases(prev => prev.map(r => ({ ...r, isRead: true })));
        
        // Dispatch custom event to notify sidebar
        window.dispatchEvent(new CustomEvent('release-notes-read'));
      } catch (error) {
        console.error('Failed to mark releases as read:', error);
      }
    }
  };

  // Don't render if no releases
  if (!isLoading && releases.length === 0) {
    return null;
  }

  const hasUnread = releases.some(r => !r.isRead);

  return (
    <Card 
      className={`overflow-hidden transition-all duration-300 ${
        hasUnread && !hasAnimated 
          ? 'animate-[fadeInBounce_0.6s_ease-out] ring-2 ring-primary/30 ring-offset-2 ring-offset-background'
          : ''
      }`}
      style={
        hasUnread && !hasAnimated
          ? { animation: 'fadeInBounce 0.6s ease-out, glowBorder 3s ease-in-out' }
          : undefined
      }
    >
      {/* Header */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between group"
      >
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-primary/10 text-primary">
            <Sparkles className="w-4 h-4" />
          </div>
          <h3 className="font-semibold text-text">What's New</h3>
          {hasUnread && (
            <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
              New
            </span>
          )}
        </div>
        <ChevronDown 
          className={`w-5 h-5 text-muted transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="mt-4 space-y-4 animate-[slideDown_0.2s_ease-out]">
          {isLoading ? (
            <>
              <ReleaseSkeleton />
              <ReleaseSkeleton />
            </>
          ) : (
            <>
              {releases.map((release) => (
                <div 
                  key={release.id}
                  className="pb-4 border-b border-border last:border-0 last:pb-0"
                >
                  {/* Version & Date */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-primary">
                        v{release.version}
                      </span>
                      {!release.isRead && (
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      )}
                    </div>
                    <span className="text-xs text-muted">
                      {formatDate(release.publishedAt)}
                    </span>
                  </div>

                  {/* Title */}
                  <h4 className="text-sm font-medium text-text mb-2">
                    {release.title}
                  </h4>

                  {/* Highlights */}
                  {release.highlights && release.highlights.length > 0 && (
                    <ul className="space-y-1 ml-4">
                      {release.highlights.map((highlight, idx) => (
                        <li 
                          key={idx}
                          className="text-xs text-muted list-disc"
                        >
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {/* View Full Changelog */}
              <Link
                to="/changelog"
                className="flex items-center justify-center gap-1.5 text-xs text-primary hover:underline py-2"
              >
                View Full Changelog
              </Link>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function ReleaseSkeleton() {
  return (
    <div className="pb-4 border-b border-border">
      <div className="flex items-center justify-between mb-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-3 w-20" />
      </div>
      <Skeleton className="h-4 w-2/3 mb-2" />
      <div className="space-y-1">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-4/5" />
      </div>
    </div>
  );
}

// Add keyframes to global CSS via inline style tag
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeInBounce {
      0% {
        opacity: 0;
        transform: translateY(-10px);
      }
      60% {
        opacity: 1;
        transform: translateY(5px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes glowBorder {
      0%, 100% {
        box-shadow: 0 0 0 0 rgba(var(--primary-rgb), 0);
      }
      50% {
        box-shadow: 0 0 20px 0 rgba(var(--primary-rgb), 0.3);
      }
    }
    
    @keyframes slideDown {
      from {
        opacity: 0;
        max-height: 0;
      }
      to {
        opacity: 1;
        max-height: 500px;
      }
    }
  `;
  document.head.appendChild(style);
}
